import { _transformCurrency } from '@freelancer/datastore/currencies';
import { transformUserStatus } from '@freelancer/datastore/users/users.transformers';
import { AddressApi } from 'api-typings/common/common';
import {
  DateOfBirthApi,
  MarketingMobileNumberApi, // tslint:disable-next-line: ordered-imports-freelancer
  MarketingMobileNumberResultApi,
  UserApi,
} from 'api-typings/users/users';
import { getDate, getMonth, getYear } from 'date-fns';
import { MarketingMobileNumber, UsersSelf } from './users-self.model';

// Transforms what the backend returns into a format your components can consume
// Should only be called in this collection's reducer (passed to `mergeEntities`)
export function transformUsersSelf(user: UserApi): UsersSelf {
  if (!user.primary_currency) {
    throw new ReferenceError('User without primary currency!');
  }
  return {
    id: user.id,
    username: user.username,
    firstName: user.first_name,
    lastName: user.last_name,
    publicName: user.public_name,
    displayName: user.display_name,
    primaryCurrency: _transformCurrency(user.primary_currency),
    address: user.address,
    dateOfBirth: user.date_of_birth,
    primaryLanguage: user.primary_language,
    hasLinkedEscrowComAccount: user.escrowcom_account_linked
      ? user.escrowcom_account_linked
      : false,
    email: user.email,
    status: user.status ? transformUserStatus(user.status) : undefined,
    marketingMobileNumber: user.marketing_mobile_number
      ? transformMarketingMobileNumber(user.marketing_mobile_number)
      : undefined,
    ...getUsersSelfComputedFields(
      user.first_name,
      user.last_name,
      user.address,
      user.date_of_birth,
    ),
    enterpriseIds: user.enterprise_ids,
  };
}

/**
 * These fields aren't returned by the backend but are computed from other
 * fields on `UsersSelf`
 */
export function getUsersSelfComputedFields(
  firstName?: string,
  lastName?: string,
  address?: AddressApi,
  dob?: DateOfBirthApi,
) {
  return {
    profileCompleted: !!firstName && !!lastName && !!address,
    escrowProfileCompleted:
      !!firstName &&
      !!lastName &&
      !!dob &&
      !!address &&
      !!address.address1 &&
      !!address.city &&
      !!address.state_code &&
      !!address.country &&
      !!address.zip,
  };
}

export function transformDOBToDMY(dob: Date): DateOfBirthApi {
  return {
    day: getDate(dob),
    month: getMonth(dob) + 1,
    year: getYear(dob),
  };
}

/** The format of fl-input uses for date is ISO String */
export function transformDMYToDOBString(dob: DateOfBirthApi): string {
  const date = new Date(dob.year, dob.month - 1, dob.day);
  return date.toISOString();
}

export function transformMarketingMobileNumberAPIResult(
  marketingMobileNumberResult: MarketingMobileNumberResultApi,
  original: UsersSelf,
): UsersSelf {
  const marketingMobileNumber = {
    phoneNumber:
      marketingMobileNumberResult.marketing_mobile_number.phone_number,
    countryCode:
      marketingMobileNumberResult.marketing_mobile_number.country_code,
    status: marketingMobileNumberResult.marketing_mobile_number.status,
  };
  return {
    ...original,
    marketingMobileNumber,
  };
}

export function transformMarketingMobileNumber(
  marketingMobileNumber: MarketingMobileNumberApi,
): MarketingMobileNumber {
  return {
    phoneNumber: marketingMobileNumber.phone_number,
    countryCode: marketingMobileNumber.country_code,
    status: marketingMobileNumber.status,
  };
}
